import { EventEmitter, inject, Injectable, signal } from '@angular/core';
import { NzModalService } from 'ng-zorro-antd/modal';
import { ToastrService } from 'ngx-toastr';
import { ZegoExpressEngine } from 'zego-express-engine-webrtc';
import ZegoLocalStream from 'zego-express-engine-webrtc/sdk/code/zh/ZegoLocalStream.web';
import { ZIMRoomAttributesDeleteConfig, ZIMRoomAttributesSetConfig } from 'zego-zim-web';

import { PopupComponent } from '../components/popup/popup.component';
import { MEDIA_STATE } from '../constants/until';
import {
  MEETING_MUTE_FLAG,
  MEETING_SHARE_SCREEN_FLAG,
  ROOM_ATTRIBUTES_KEY,
} from '../pages/meeting-ui/types/constant';
import { RequestOnOffCamUsers, RequestOnOffMicUsers } from '../pages/meeting-ui/types/meeting';
import { SOUND_LEVEL_THRESHOLD, SOUND_LEVEL_UPDATE_INTERVAL } from '../utils/meeting.util';
import { LocalMediaPermissionService } from './local-media-permission.service';
import { ZegoEngineService } from './zego-engine.service';
import { ZegoStreamService } from './zego-stream.service';
import { ZimService } from './zim.service';

export interface MediaState {
  isMicEnabled: boolean;
  isCameraEnabled: boolean;
  isScreenSharing: boolean;
  soundLevel: number;
  isSpeaking: boolean;
}

@Injectable({
  providedIn: 'root',
})
export class ZegoMediaControlService {
  private readonly zimService = inject(ZimService);
  private readonly zegoEngineService = inject(ZegoEngineService);
  private localMediaPermissionService = inject(LocalMediaPermissionService);
  private zegoStreamService = inject(ZegoStreamService);
  private toastService = inject(ToastrService);
  private engine: ZegoExpressEngine;
  private modal = inject(NzModalService);

  private _mediaState = signal<MediaState>({
    isMicEnabled: false,
    isCameraEnabled: false,
    isScreenSharing: false,
    soundLevel: 0,
    isSpeaking: false,
  });

  readonly mediaState = this._mediaState.asReadonly();

  screenStream?: ZegoLocalStream;
  screenStreamID?: string;
  private screenStreamSeq?: number;
  private screenPreviewTimer: ReturnType<typeof setTimeout> | null = null;

  public requestOnOffMicUsers: RequestOnOffMicUsers = {};
  public requestOnOffCamUsers: RequestOnOffCamUsers = {};

  public readonly micCamAttrsChanged = new EventEmitter<void>();

  /** Quyền chia sẻ màn hình của cả phòng do host điều khiển. Mặc định cho phép. */
  private _allowShareScreen = signal(true);
  readonly allowShareScreen = this._allowShareScreen.asReadonly();

  constructor() {
    this.engine = this.zegoEngineService.engine;
  }

  setMediaState(micState: boolean, camState: boolean) {
    this._mediaState.set({
      ...this.mediaState(),
      isMicEnabled: micState,
      isCameraEnabled: camState,
    });
  }

  // Keep the request on/off mic/cam maps in sync with the latest room attributes
  // so that whoever becomes host next already has the current state.
  setRequestOnOffMicUsers(users: RequestOnOffMicUsers) {
    this.requestOnOffMicUsers = users;
    this.micCamAttrsChanged.emit();
  }

  setRequestOnOffCamUsers(users: RequestOnOffCamUsers) {
    this.requestOnOffCamUsers = users;
    this.micCamAttrsChanged.emit();
  }

  setAllowShareScreen(isAllow: boolean) {
    this._allowShareScreen.set(isAllow);
  }

  // Toggle Microphone
  toggleMicrophone(): boolean {
    const newState = !this.mediaState().isMicEnabled;

    this.engine.muteMicrophone(!newState);

    this._mediaState.set({
      ...this.mediaState(),
      isMicEnabled: newState,
    });

    console.log(
      'Local stream flow: mute publish - Microphone state:',
      newState ? 'enabled' : 'disabled',
    );
    return newState;
  }

  // Toggle Camera
  toggleCamera(localStream: ZegoLocalStream): boolean {
    const newState = !this.mediaState().isCameraEnabled;

    this.engine.enableVideoCaptureDevice(localStream, newState);

    this._mediaState.set({
      ...this.mediaState(),
      isCameraEnabled: newState,
    });

    console.log(
      'Local stream flow: mute publish - Camera state:',
      newState ? 'enabled' : 'disabled',
    );
    return newState;
  }

  async toggleScreenShare(
    userID: string,
    screenPreviewElement?: HTMLDivElement,
    isHost = false,
  ): Promise<boolean> {
    if (!this.mediaState().isScreenSharing) {
      return await this.startScreenShare(userID, screenPreviewElement, isHost);
    } else {
      return await this.stopScreenShare();
    }
  }

  private async startScreenShare(
    userID: string,
    screenPreviewElement?: HTMLDivElement,
    isHost = false,
  ): Promise<boolean> {
    try {
      // Create screen stream using Zego's method
      const streamResult = await this.zegoStreamService.createAndPublishStream(
        userID,
        false,
        false,
        true,
      );

      if (!streamResult?.localStream || !streamResult.streamID) {
        return true;
      }

      // Create new stream ID for screen share
      this.screenStreamID = streamResult.streamID;
      this.screenStreamSeq = streamResult.seq;
      const currentScreenStreamID = streamResult.streamID;

      // Handle when user stops sharing via browser UI
      const videoTrack = streamResult.localStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.onended = () => {
          this.stopScreenShare(currentScreenStreamID);
        };
      }

      this.screenStream = streamResult.localStream;

      // Update state
      this._mediaState.set({
        ...this.mediaState(),
        isScreenSharing: true,
      });

      // Host có thể đã tắt quyền chia sẻ trong lúc user còn đang chọn màn hình
      if (!isHost && !this.allowShareScreen()) {
        await this.stopScreenShare();
        this.modal.create({
          nzContent: PopupComponent,
          nzData: {
            action: 'Không thể chia sẻ màn hình',
            note: 'Người tổ chức đã tắt quyền chia sẻ màn hình của tất cả thành viên. Bạn sẽ có thể chia sẻ khi người tổ chức mở lại.',
            actionOnOk: 'Đã hiểu',
            type: 'error',
          },
          nzCentered: true,
          nzWidth: 460,
          nzFooter: null,
          nzClosable: true,
        });
        return true;
      }

      // Preview screen locally
      if (screenPreviewElement && this.screenStream) {
        this.clearScreenPreviewTimer();
        this.screenPreviewTimer = setTimeout(() => {
          this.screenPreviewTimer = null;
          // Có thể đã dừng share trong 100ms chờ -> stream đã bị destroy
          if (!this.screenStream) return;

          this.screenStream.playVideo(screenPreviewElement, {
            enableAutoplayDialog: false,
            objectFit: 'contain',
          });
        }, 100);
      }

      console.log('Screen sharing started:', this.screenStreamID);
      return true;
    } catch (error: any) {
      console.error('Error starting screen share:', error);
      if (error && error.errorCode === 1103010) {
        if (error.extendedData?.includes('NotAllowedError')) {
          this.localMediaPermissionService.handleNotAllow('share-screen');
        }
        return true;
      }
      if (error && error.errorCode === 1103002) {
        this.toastService.error('Trình duyệt không hỗ trợ');
        return true;
      }
      return false;
    }
  }

  async stopScreenShare(expectedStreamID?: string): Promise<boolean> {
    if (!this.screenStream || !this.screenStreamID) {
      return false;
    }

    if (expectedStreamID && this.screenStreamID !== expectedStreamID) {
      return false;
    }

    try {
      this.clearScreenPreviewTimer();
      this.zegoStreamService.stopPublishingStream(this.screenStreamID, this.screenStream);

      this.screenStream = undefined;
      this.screenStreamID = undefined;
      this.screenStreamSeq = undefined;

      // Update state
      this._mediaState.set({
        ...this.mediaState(),
        isScreenSharing: false,
      });

      console.log('Screen sharing stopped');
      return true;
    } catch (error) {
      console.error('Error stopping screen share:', error);
      return false;
    }
  }

  getScreenStreamID(): string | undefined {
    return this.screenStreamID;
  }

  getScreenStreamSeq(): number | undefined {
    return this.screenStreamSeq;
  }

  //Mute remote user mic
  async muteClientMedia(
    userIds: string[],
    roomId: string,
    type: 'MIC' | 'CAM',
    isMuteAll = false,
    flag: MEETING_MUTE_FLAG = MEETING_MUTE_FLAG.HOST_AND_USERS,
  ) {
    const zim = this.zimService.getZIM()!;
    const config: ZIMRoomAttributesSetConfig = {
      isUpdateOwner: true,
      isForce: true,
      isDeleteAfterOwnerLeft: false,
    };
    if (isMuteAll) {
      if (zim) {
        let attributes;
        let requestKey: string;
        if (type === 'MIC') {
          this.requestOnOffMicUsers = {};
          requestKey = ROOM_ATTRIBUTES_KEY.REQUEST_ON_OFF_MIC_USERS;
          attributes = {
            [ROOM_ATTRIBUTES_KEY.MUTE_ALL_MIC]: JSON.stringify({
              flag: flag,
              version: this.getMicVersion(),
            }),
          };
        } else {
          this.requestOnOffCamUsers = {};
          requestKey = ROOM_ATTRIBUTES_KEY.REQUEST_ON_OFF_CAM_USERS;
          attributes = {
            [ROOM_ATTRIBUTES_KEY.MUTE_ALL_CAM]: JSON.stringify({
              flag: flag,
              version: this.getCamVersion(),
            }),
          };
        }
        const deleteConfig: ZIMRoomAttributesDeleteConfig = { isForce: true };
        try {
          await zim.deleteRoomAttributes([requestKey], roomId, deleteConfig);
        } catch (error) {
          console.error('Error deleting room attribute:', requestKey, error);
        }
        zim.setRoomAttributes(attributes, roomId, config);
      }
    } else {
      // Single user mute
      let attributes;
      if (type === 'MIC') {
        this.requestOnOffMicUsers = {
          ...this.requestOnOffMicUsers,
          [`${userIds[0]}`]: JSON.stringify({
            flag: MEDIA_STATE.OFF,
            mic_version: this.getInviteVersion(),
          }),
        };
        attributes = {
          [ROOM_ATTRIBUTES_KEY.REQUEST_ON_OFF_MIC_USERS]: JSON.stringify(this.requestOnOffMicUsers),
        };
      } else {
        this.requestOnOffCamUsers = {
          ...this.requestOnOffCamUsers,
          [`${userIds[0]}`]: JSON.stringify({
            flag: MEDIA_STATE.OFF,
            cam_version: this.getInviteVersion(),
          }),
        };
        attributes = {
          [ROOM_ATTRIBUTES_KEY.REQUEST_ON_OFF_CAM_USERS]: JSON.stringify(this.requestOnOffCamUsers),
        };
      }
      zim.setRoomAttributes(attributes, roomId, config);
    }
  }

  /**
   * Host bật/tắt quyền chia sẻ màn hình cho toàn bộ thành viên. Ghi 1 room attribute
   * chung (không tách theo userID như mic/cam) kèm version để client bỏ qua giá trị cũ.
   */
  async requestOnOffShareScreen(roomId: string, isAllow: boolean) {
    const zim = this.zimService.getZIM();
    if (!zim || !roomId) return;

    const attributes = {
      [ROOM_ATTRIBUTES_KEY.MUTE_ALL_SCREEN]: JSON.stringify({
        flag: isAllow
          ? MEETING_SHARE_SCREEN_FLAG.HOST_AND_USERS
          : MEETING_SHARE_SCREEN_FLAG.HOST_AND_CO_HOST,
        version: this.getInviteVersion(),
      }),
    };
    const config: ZIMRoomAttributesSetConfig = {
      isUpdateOwner: true,
      isForce: true,
      isDeleteAfterOwnerLeft: false,
    };
    await zim.setRoomAttributes(attributes, roomId, config);
  }

  private getInviteVersion(): string {
    return 'i_' + new Date().getTime();
  }

  private getMicVersion(): string {
    return 'm_' + new Date().getTime();
  }

  private getCamVersion(): string {
    return 'c_' + new Date().getTime();
  }

  async requestOpenCamMic(userId: string, roomId: string, type: 'MIC' | 'CAM') {
    const zim = this.zimService.getZIM()!;
    if (zim) {
      let attributes;
      if (type === 'MIC') {
        this.requestOnOffMicUsers = {
          ...this.requestOnOffMicUsers,
          [`${userId}`]: JSON.stringify({
            flag: MEDIA_STATE.ON,
            mic_version: this.getInviteVersion(),
          }),
        };
        attributes = {
          [ROOM_ATTRIBUTES_KEY.REQUEST_ON_OFF_MIC_USERS]: JSON.stringify(this.requestOnOffMicUsers),
        };
      } else {
        this.requestOnOffCamUsers = {
          ...this.requestOnOffCamUsers,
          [`${userId}`]: JSON.stringify({
            flag: MEDIA_STATE.ON,
            cam_version: this.getInviteVersion(),
          }),
        };
        attributes = {
          [ROOM_ATTRIBUTES_KEY.REQUEST_ON_OFF_CAM_USERS]: JSON.stringify(this.requestOnOffCamUsers),
        };
      }

      const config: ZIMRoomAttributesSetConfig = {
        isUpdateOwner: true,
        isForce: true,
        isDeleteAfterOwnerLeft: false,
      };
      zim.setRoomAttributes(attributes, roomId, config);
    }
  }

  lastTimeUpdateSoundLevel = 0;
  updateLocalSoundLevel(soundLevel: number) {
    if (Date.now() - this.lastTimeUpdateSoundLevel < SOUND_LEVEL_UPDATE_INTERVAL) {
      return;
    }

    this.lastTimeUpdateSoundLevel = Date.now();

    this._mediaState.set({
      ...this.mediaState(),
      soundLevel: soundLevel,
      isSpeaking: soundLevel > SOUND_LEVEL_THRESHOLD,
    });
  }

  resetState() {
    this.clearScreenPreviewTimer();
    this._mediaState.set({
      isMicEnabled: false,
      isCameraEnabled: false,
      isScreenSharing: false,
      soundLevel: 0,
      isSpeaking: false,
    });
  }

  private clearScreenPreviewTimer(): void {
    if (this.screenPreviewTimer) {
      clearTimeout(this.screenPreviewTimer);
      this.screenPreviewTimer = null;
    }
  }
}
